import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';
import { cn } from '../lib/utils';
import { Menu } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const systems = [
    { 
      category: 'Classical',
      glyph: '◉',
      items: [
        { to: '/pythagorean', title: 'Pythagorean', glyph: '△', desc: 'Harmony of numbers' },
        { to: '/chaldean', title: 'Chaldean', glyph: '☽', desc: 'Babylonian wisdom' },
        { to: '/plato', title: 'Plato', glyph: '◯', desc: 'Sacred geometry' },
      ]
    },
    {
      category: 'Celestial',
      glyph: '✦',
      items: [
        { to: '/western', title: 'Western', glyph: '●', desc: 'Tropical zodiac' },
        { to: '/vedic', title: 'Vedic', glyph: '॰', desc: 'Sidereal wisdom' },
        { to: '/bazi', title: 'BaZi', glyph: '䷀', desc: 'Four Pillars' },
      ]
    },
    {
      category: 'Mystical',
      glyph: '✼',
      items: [
        { to: '/gematria', title: 'Gematria', glyph: '✯', desc: 'Hebrew codes' },
        { to: '/agrippa', title: 'Agrippa', glyph: '※', desc: 'Renaissance magic' },
        { to: '/sepharial', title: 'Sepharial', glyph: '⊙', desc: 'Degree meanings' },
      ]
    },
    {
      category: 'Hermetic',
      glyph: '❀',
      items: [
        { to: '/enochian', title: 'Enochian', glyph: '⛤', desc: 'Angelic language' },
        { to: '/eliphas-levi', title: 'Levi', glyph: '⚹', desc: 'Tarot magic' },
        { to: '/liber777', title: 'Liber 777', glyph: '∞', desc: 'Qabalah' },
        { to: '/abramelin', title: 'Abramelin', glyph: '✪', desc: 'Sacred magic' },
      ]
    }
  ];

  return (
    <nav className="sticky top-0 z-50 w-full sacred-geometry-bg border-b border-alchemical bg-white/90 backdrop-blur">
      <div className="container flex h-16 items-center px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-3 mr-8">
          <span className="font-mystical text-2xl number-mystical">
            ☉
          </span>
          <span className="font-mystical text-lg tracking-wider hidden sm:block">
            CALCULATIONS
          </span>
        </Link>

        {/* Desktop Navigation */}
        <NavigationMenu className="hidden lg:flex">
          <NavigationMenuList>
            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link
                  to="/profile"
                  className={cn(
                    "font-philosophical text-sm text-gray-700 hover:text-gray-900 px-3 py-2 inline-flex items-center",
                    location.pathname === '/profile' && "text-gray-900 font-medium"
                  )}
                >
                  <span className="mr-2">☉</span>
                  Cosmic Profile
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>
            <NavigationMenuItem>
              <NavigationMenuTrigger className="font-philosophical text-gray-700 hover:text-gray-900">
                <span className="mr-2">✦</span>
                Systems
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <div className="w-[700px] p-6 sacred-geometry-bg">
                  <div className="grid gap-6">
                    {systems.map((category) => (
                      <div key={category.category}>
                        <h3 className="font-mystical text-sm mb-3 flex items-center text-gray-600">
                          <span className="mr-2 text-lg">{category.glyph}</span>
                          {category.category}
                        </h3>
                        <ul className="grid grid-cols-3 gap-3">
                          {category.items.map((item) => (
                            <li key={item.to}>
                              <NavigationMenuLink asChild>
                                <Link
                                  to={item.to}
                                  className={cn(
                                    "block p-3 rounded-md transition-all hover:bg-gradient-to-r hover:from-transparent hover:to-yellow-50 border border-transparent hover:border-alchemical",
                                    location.pathname === item.to && "bg-gradient-to-r from-transparent to-yellow-50 border-alchemical"
                                  )}
                                >
                                  <div className="flex items-start">
                                    <span className="text-xl mr-2 sacred-number">{item.glyph}</span>
                                    <div>
                                      <div className="font-philosophical text-sm font-medium">{item.title}</div>
                                      <p className="font-ancient text-xs text-gray-500 mt-1">
                                        {item.desc}
                                      </p>
                                    </div>
                                  </div>
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        {/* Quick Links */}
        <div className="hidden md:flex items-center space-x-6 ml-auto mr-6">
          <Link 
            to="/faq" 
            className="font-philosophical text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <span className="glyph-mercury mr-1"></span>
            FAQ
          </Link>
          <Link 
            to="/terms" 
            className="font-philosophical text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <span className="glyph-saturn mr-1"></span>
            Terms
          </Link>
        </div>

        {/* Mobile Menu */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger className="lg:hidden ml-auto">
            <Menu className="h-6 w-6" />
          </SheetTrigger>
          <SheetContent side="right" className="w-80 sacred-geometry-bg">
            <SheetHeader>
              <SheetTitle className="font-mystical text-xl">
                Systems
              </SheetTitle>
            </SheetHeader>
            <div className="mt-6 space-y-4">
              <Link
                to="/profile"
                onClick={() => setIsOpen(false)}
                className={cn(
                  "block py-2 font-mystical text-sm hover:text-yellow-600 transition-colors",
                  location.pathname === '/profile' && "text-yellow-600"
                )}
              >
                <span className="mr-2">☉</span>
                Cosmic Profile
              </Link>
              <div className="divider-mystical my-2"></div>
              {systems.map((category) => (
                <div key={category.category}>
                  <h3 className="font-mystical text-sm mb-2 text-gray-600 flex items-center">
                    <span className="mr-2">{category.glyph}</span>
                    {category.category}
                  </h3>
                  <div className="space-y-2 ml-6">
                    {category.items.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setIsOpen(false)}
                        className={cn(
                          "block py-2 font-philosophical text-sm hover:text-yellow-600 transition-colors",
                          location.pathname === item.to && "text-yellow-600"
                        )}
                      >
                        <span className="mr-2">{item.glyph}</span>
                        {item.title}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
              
              <div className="divider-mystical my-4"></div>
              
              <div className="space-y-2">
                <Link
                  to="/faq"
                  onClick={() => setIsOpen(false)}
                  className="block py-2 font-philosophical text-sm"
                >
                  <span className="glyph-mercury mr-2"></span>
                  FAQ
                </Link>
                <Link
                  to="/terms"
                  onClick={() => setIsOpen(false)}
                  className="block py-2 font-philosophical text-sm"
                >
                  <span className="glyph-saturn mr-2"></span>
                  Terms
                </Link>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
}